import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TagActionsMenu } from '../../SpecialTag/TagActionsMenu';
import { tagMenuAction, variant } from '../../SpecialTag/types';
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { useAuth } from '../../../../hooks/useAuth';
import { useSurveillanceRoutes } from '../../../../hooks/useSurveillanceRoutes';
import { canLinkExternalOrder } from '../../../../utils/surveillanceRoute';
import { UnlinkedWorkRouteRef } from '../../../../types/surveillanceRoute';

type UnlinkedRoutePillsProps = {
  routes?: UnlinkedWorkRouteRef[];
  /** OT padre: la que se liga al recorrido. */
  parentOrderId: string;
  revalidatorFunction?: () => void;
};

/**
 * Spec § 16: etiqueta ámbar «Treball de Su.NN · sense vincular» en la card de
 * un trabajo que hizo, sin duda, una patrulla cuyo recorrido NO está ligado a
 * la OT. Es la red de seguridad de lo que el autovínculo no liga: avisa y
 * deja decidir, nunca liga por su cuenta.
 *
 * Quien puede ligar (Sala, COEX, capataz) tiene «Vincular recorrido a la OT»
 * y «Ver recorrido»; el resto, solo ver (un toque lleva al recorrido). Al
 * ligar, se recarga el detalle y la etiqueta pasa a ser la pill verde.
 */
export const UnlinkedRoutePills = ({
  routes = [],
  ...rest
}: UnlinkedRoutePillsProps) =>
  // Sin avisos no se montan los hooks: hay una card por cada hija de la OT.
  routes.length > 0 ? <UnlinkedRoutePillList routes={routes} {...rest} /> : null;

const UnlinkedRoutePillList = ({
  routes = [],
  parentOrderId,
  revalidatorFunction,
}: UnlinkedRoutePillsProps) => {
  const { t } = useTranslation(['pages']);
  const navigate = useNavigate();
  const isLandscapeTablet = useIsLandscapeTablet();
  const { currentRole } = useAuth();
  const { linkOrderToSurveillanceRoute } = useSurveillanceRoutes();
  const canLink = canLinkExternalOrder(currentRole);
  // Un segundo toque mientras liga no manda otra petición.
  const linking = useRef(false);

  const link = async (routeId: string) => {
    if (linking.current) return;
    linking.current = true;
    try {
      await linkOrderToSurveillanceRoute(routeId, parentOrderId);
      revalidatorFunction?.();
    } catch {
      // El hook ya ha avisado con su toast; la etiqueta se queda.
    } finally {
      linking.current = false;
    }
  };

  // Un mismo recorrido una sola vez, aunque la patrulla hiciera varias cosas.
  const unique = routes.filter(
    (ref, i) => routes.findIndex((r) => r.routeId === ref.routeId) === i
  );
  return (
    <>
      {unique.map((ref) => {
        const actions: tagMenuAction[] = [
          ...(canLink
            ? [
                {
                  icon: 'link' as const,
                  // Con varios trabajos del recorrido, dice cuántos se ligan
                  // de una vez: ligar es de la OT entera, no de esta card.
                  label:
                    (ref.workCount ?? 1) > 1
                      ? t('pages:surveillance.linkRouteToOrderWorks', {
                          count: ref.workCount,
                        })
                      : t('pages:surveillance.linkRouteToOrder'),
                  onClick: () => void link(ref.routeId),
                },
              ]
            : []),
          {
            icon: 'view',
            label: t('pages:surveillance.seeRoute'),
            onClick: () => navigate(`/surveillance-route/${ref.routeId}`),
          },
        ];
        return (
          <TagActionsMenu
            key={ref.routeId}
            variant={variant.UnlinkedWork}
            sizeTag={isLandscapeTablet ? 'small' : 'default'}
            title={t('pages:surveillance.workOfUnlinkedRoute', {
              roid: ref.roid || '',
            })}
            actions={actions}
            testId="unlinked-route-pill"
          />
        );
      })}
    </>
  );
};
